import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { AuthEnums } from "models/enums";
import useAPIClientHelper from "./useAPIClientHelper";
import { CompanyService } from "services";
import { WintSuperkollAPIModelTimeReporting as ApiModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { IconDefinitionConstants, ServiceConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ThunkDispatch } from "redux-thunk";
import { loadInvoiceDimensions } from "store/actions/commonActions";

function useTimeReportingProjectService() {
  const { apiClientInstance } = useAPIClientHelper();

  const hasCreatePermissions = () => {
    const createPermissions =
      AuthEnums.PersonCompanyRelationType.InvoicesAdmin |
      AuthEnums.PersonCompanyRelationType.Invoices |
      AuthEnums.PersonCompanyRelationType.Admin;

    const hasPermission = CompanyService.hasRelation(createPermissions);
    return CompanyService.isTimeReportingEnabled() && hasPermission;
  };

  const hasEditPermissions = () => {
    return hasCreatePermissions();
  };

  const hasReadOnlyPermissions = () => {
    const readOnlyPermissions = AuthEnums.PersonCompanyRelationType.Auditor;
    const hasReadOnlyPermission = CompanyService.hasRelation(readOnlyPermissions);
    return CompanyService.isTimeReportingEnabled() && !hasCreatePermissions() && hasReadOnlyPermission;
  };

  const getTimeReportingProjectsReport = async (
    pagination: WintUtilsWebApi.Filter,
    filter: TimeReportingProjectFormTypes.Filter
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company).TimeReportingProjectClient.Report({
      ...pagination,
      ...reportToAPIFilter(filter),
    });
  };

  const reportToAPIFilter = (
    formFilter?: TimeReportingProjectFormTypes.Filter
  ): Omit<ApiModel.TimeReportingProjectFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      projectName: formFilter?.projectName,
      from:
        formFilter?.period !== undefined
          ? DateHelper.resetTime(DateHelper.getFirstDayOfMonth(formFilter?.period))
          : undefined,
      to:
        formFilter?.period !== undefined
          ? DateHelper.resetTime(DateHelper.getLastDayOfMonth(formFilter?.period))
          : undefined,
      includeInactiveProjects: formFilter?.includeInactiveProjects,
      includeDeletedProjects: formFilter?.includeDeletedProjects,
      onlyWithLeftToInvoice: formFilter?.onlyWithLeftToInvoice,
      customerId: formFilter?.customerId,
      projectMemberId: formFilter?.projectMemberId,
      businessUnitId: formFilter?.businessUnitId,
    };
  };

  const isProjectNameDuplicate = async (projectName: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company).TimeReportingProjectClient.DuplicateNameCheck(projectName);
  };

  const getTimeReportingProject = async (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .TimeReportingProjectClient.Get(id)
      .then((res) => res.data);
  };

  const createTimeReportingProject = (
    timeReportingProject: ApiModel.TimeReportingProjectPost,
    dispatch: ThunkDispatch<any, any, any>
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportingProjectClient.Post(timeReportingProject)
      .then(async (res) => {
        dispatch(loadInvoiceDimensions());
        return res;
      });
  };

  const inactivateTimeReportingProject = async (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).TimeReportingProjectClient.Inactivate(id);
  };

  const deleteTimeReportingProject = async (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).TimeReportingProjectClient.Delete(id);
  };

  const hasUnInvoicedProjectTime = async (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportingProjectClient.HasUnInvoicedProjectTime(id)
      .then((res) => res.data);
  };

  const updateTimeReportingProject = (
    id: string,
    timeReportingProject: ApiModel.TimeReportingProjectPut,
    dispatch: ThunkDispatch<any, any, any>
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportingProjectClient.Put(id, timeReportingProject)
      .then(async (res) => {
        dispatch(loadInvoiceDimensions());
        return res;
      });
  };

  const searchAvailableProjectMembers = (searchStr: string, excludeIds?: number[]) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportingProjectClient.AvailableProjectMembers({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        excludeSelf: false,
      })
      .then((res) => {
        return res.data.items ? res.data.items.filter((p) => p.id && !excludeIds?.includes(p.id)) : [];
      });
  };

  const searchAvailableProjectMembersAutoComplete = (searchStr: string, excludeIds?: number[]) => {
    return searchAvailableProjectMembers(searchStr, excludeIds).then((result) =>
      result.map(
        (employee): AutocompleteListItem => ({
          id: employee.id,
          value: employee.name ?? "",
          icon: IconDefinitionConstants.WUser,
          data: employee,
        })
      )
    );
  };

  const searchAvailableProjectManagers = (searchStr: string, excludeSelf?: boolean) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportingProjectClient.AvailableProjectManagers({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        excludeSelf: excludeSelf ?? true,
      })
      .then((res) => (res.data.items ? res.data.items : []));
  };

  const getTimeReportingInfo = (searchStr: string, excludeSelf?: boolean) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportingProjectClient.AvailableProjectManagers({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        excludeSelf: excludeSelf ?? true,
      })
      .then((res) => (res.data.items ? res.data.items : []));
  };

  const searchAvailableProjectManagersAutoComplete = (searchStr: string, excludeSelf?: boolean) => {
    return searchAvailableProjectManagers(searchStr, excludeSelf).then((result) =>
      result.map(
        (employee): AutocompleteListItem => ({
          id: employee.id,
          value: employee.name ?? "",
          icon: IconDefinitionConstants.WUser,
        })
      )
    );
  };

  const createInvoiceDrafts = async (id: string, invoiceDocumentation: ApiModel.TimeReportInvoiceDocumentation) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).TimeReportingProjectClient.Invoice(id, invoiceDocumentation);
  };

  const getMemberBillingModelText = (billingModel?: ApiModel.TimeReportingPersonProjectBillingModel) => {
    return LocalizationHelperInstance.getTranslatedEnumValue("TimeReportingProject.BillingModel", billingModel ?? 0);
  };

  const getProjectCategories = (searchStr: string, projectId: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportingProjectClient.Categories({
        ...ServiceConstants.DefaultFilter,
        projectId: projectId,
        searchStr: searchStr,
      })
      .then((result) => {
        return result.data.items ?? [];
      });
  };

  const getProjectCategoriesAutoComplete = (searchStr: string, projectId: string) => {
    return getProjectCategories(searchStr, projectId).then((result) =>
      result.map(
        (category): AutocompleteListItem => ({
          id: category.id,
          value: category.name ?? "",
        })
      )
    );
  };

  const connectInvoice = async (id: string, invoiceDocumentation: ApiModel.ConnectInvoiceDocumentation) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).TimeReportingProjectClient.ConnectInvoice(id, invoiceDocumentation);
  };

  return {
    hasCreatePermissions,
    hasEditPermissions,
    hasReadOnlyPermissions,
    getTimeReportingProjectsReport,
    isProjectNameDuplicate,
    getTimeReportingProject,
    createTimeReportingProject,
    inactivateTimeReportingProject,
    deleteTimeReportingProject,
    hasUnInvoicedProjectTime,
    updateTimeReportingProject,
    searchAvailableProjectMembers,
    searchAvailableProjectMembersAutoComplete,
    searchAvailableProjectManagers,
    getTimeReportingInfo,
    searchAvailableProjectManagersAutoComplete,
    createInvoiceDrafts,
    getMemberBillingModelText,
    getProjectCategories,
    getProjectCategoriesAutoComplete,
    connectInvoice,
    reportToAPIFilter,
  };
}

export default useTimeReportingProjectService;
